import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useStore } from '../../store/useStore';
import SearchBar from '../ui/SearchBar';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const location = useLocation();
  const { watchlist } = useStore();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // One calm membrane: Home carries catalog discovery while Cable and Sports
  // keep their distinct interaction models. Deep movie/TV/anime routes remain
  // available without competing for permanent navigation weight.
  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/cable', label: '📺 Cable' },
    { path: '/sports', label: '🏀 Sports' },
    { path: '/watchlist', label: 'Watchlist' },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Shared renderer so the desktop nav can interleave the Browse dropdown
  // between top-level links without duplicating link markup.
  const renderNavLink = (link: { path: string; label: string }) => (
    <Link
      key={link.path}
      to={link.path}
      className={`text-sm font-medium transition-colors relative ${
        isActive(link.path) ? 'text-white' : 'text-gray-300 hover:text-white'
      }`}
    >
      {link.label}
      {link.path === '/watchlist' && watchlist.length > 0 && (
        <span className="absolute -top-2 -right-4 bg-[var(--color-primary)] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
          {watchlist.length > 9 ? '9+' : watchlist.length}
        </span>
      )}
      {isActive(link.path) && (
        <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[var(--color-primary)] rounded-full" />
      )}
    </Link>
  );

  return (
    // Navbar IS the somatic membrane between visitor and content. The previous
    // `from-black/80 to-transparent` gradient let page h1 elements bleed
    // through and made click targets unreliable. Solid background + backdrop-
    // blur preserves the cinematic feel while keeping the membrane impermeable.
    <nav
      style={{ borderRadius: 0 }}
      className={`liquid-glass fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-lg' : ''
      }`}
    >
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center"
          >
            <img
              src="/logo-banner.png"
              alt="CineVault"
              className="h-12 sm:h-14 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            {navLinks.map(renderNavLink)}
          </div>

          {/* Search and Actions */}
          <div className="flex items-center gap-3 sm:gap-4">
            <SearchBar />

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {showMobileMenu ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="lg:hidden py-4 border-t border-white/10 animate-slide-up">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setShowMobileMenu(false)}
                className={`block py-3 px-4 rounded-lg transition-colors ${
                  isActive(link.path)
                    ? 'bg-white/10 text-white'
                    : 'text-gray-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}
